import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate, formatDateTime } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FileText, Plus, Trash2, Edit } from 'lucide-react';
import { FilterBar, SelectFilter, DateRangeFilter, ClearFilters, type Option } from '@/components/filters';
import { buildQuery, type DateRange } from '@/lib/filters';
import { StatTiles } from '@/components/StatTiles';
import { PageHeader } from '@/components/PageHeader';
import { Pagination, DEFAULT_PAGE_SIZE } from '@/components/Pagination';
import { StatusBadge } from '@/components/StatusBadge';

interface PurchaseOrder {
  id: string;
  poNumber: string;
  customerName?: string;
  partnerName?: string;
  customerId?: string;
  partnerId?: string;
  totalAmount: number;
  currency?: string;
  status: string;
  createdAt?: number;
  created_at?: number;
  approvedAt?: number;
  approved_at?: number;
}

function PurchaseOrders() {
  const { t } = useTranslation();
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedPO, setSelectedPO] = useState<PurchaseOrder | null>(null);
  const [newPO, setNewPO] = useState({
    poNumber: '',
    customerId: '',
    partnerId: '',
    amount: 0,
    status: 'PENDING',
  });
  const [customers, setCustomers] = useState<Array<{ id: string; name: string }>>([]);
  const [partners, setPartners] = useState<Array<{ id: string; name: string }>>([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [partnerFilter, setPartnerFilter] = useState('all');
  const [dateRange, setDateRange] = useState<DateRange>({});

  useEffect(() => {
    fetchPurchaseOrders();
  }, [statusFilter, customerFilter, partnerFilter, dateRange.from, dateRange.to, page]);

  // A filter change invalidates the current page — otherwise you can sit on
  // page 5 of a result set that now has one page and see an empty table.
  useEffect(() => { setPage(1); }, [statusFilter, customerFilter, partnerFilter, dateRange.from, dateRange.to]);

  useEffect(() => {
    fetchCustomersAndPartners();
  }, []);

  const fetchCustomersAndPartners = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const headers = { Authorization: `Bearer ${token}` };
      const [cRes, pRes] = await Promise.all([
        fetch(`${apiUrl}/api/customers?limit=1000`, { headers }),
        fetch(`${apiUrl}/api/partners?limit=1000`, { headers }),
      ]);
      if (cRes.ok) {
        const j = await cRes.json();
        setCustomers((j.data || []).map((c: any) => ({ id: c.id, name: c.name })));
      }
      if (pRes.ok) {
        const j = await pRes.json();
        const list = j.data?.partners ?? j.data ?? [];
        setPartners(list.map((p: any) => ({ id: p.id, name: p.name })));
      }
    } catch (error) {
      console.error('Failed to fetch customers/partners:', error);
    }
  };

  const clearFilters = () => {
    setStatusFilter('all');
    setCustomerFilter('all');
    setPartnerFilter('all');
    setDateRange({});
  };

  const fetchPurchaseOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const qs = buildQuery({
        limit: DEFAULT_PAGE_SIZE,
        page,
        status: statusFilter,
        customer_id: customerFilter,
        partner_id: partnerFilter,
        from: dateRange.from,
        to: dateRange.to,
      });
      const response = await fetch(`${apiUrl}/api/purchase-orders${qs}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPurchaseOrders(data.data?.purchase_orders || data.data || []);
        setTotal(data.data?.total ?? 0);
      }
    } catch (error) {
      console.error('Failed to fetch purchase orders:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreatePO = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      // The PO service requires a non-empty items[] and computes the total from it.
      // Represent the entered amount as a single line so the total matches.
      const body = {
        po_number: newPO.poNumber,
        customer_id: newPO.customerId || null,
        partner_id: newPO.partnerId || null,
        status: newPO.status,
        items: [{ description: 'Purchase order total', quantity: 1, price: newPO.amount }],
      };
      const response = await fetch(`${apiUrl}/api/purchase-orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        setIsCreateDialogOpen(false);
        setNewPO({
          poNumber: '',
          customerId: '',
          partnerId: '',
          amount: 0,
          status: 'PENDING',
        });
        fetchPurchaseOrders();
      }
    } catch (error) {
      console.error('Failed to create purchase order:', error);
    }
  };

  const openViewDialog = (po: PurchaseOrder) => {
    setSelectedPO(po);
    setIsViewDialogOpen(true);
  };

  const openEditDialog = (po: PurchaseOrder) => {
    setSelectedPO({ ...po });
    setIsEditDialogOpen(true);
  };

  const handleEditPO = async () => {
    if (!selectedPO) return;

    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/purchase-orders/${selectedPO.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          poNumber: selectedPO.poNumber,
          customerId: selectedPO.customerId ?? null,
          partnerId: selectedPO.partnerId ?? null,
          totalAmount: selectedPO.totalAmount,
          status: selectedPO.status,
        }),
      });

      if (response.ok) {
        setIsEditDialogOpen(false);
        setSelectedPO(null);
        fetchPurchaseOrders();
      }
    } catch (error) {
      console.error('Failed to update purchase order:', error);
    }
  };

  const openDeleteDialog = (po: PurchaseOrder) => {
    setSelectedPO(po);
    setIsDeleteDialogOpen(true);
  };

  const handleDeletePO = async () => {
    if (!selectedPO) return;

    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/purchase-orders/${selectedPO.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setIsDeleteDialogOpen(false);
        setSelectedPO(null);
        fetchPurchaseOrders();
      }
    } catch (error) {
      console.error('Failed to delete purchase order:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full px-4 py-6 sm:px-6 lg:px-8">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">{t('common.loading')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        eyebrow="Partners"
        icon={FileText}
        title={t('purchaseOrders.title')}
        subtitle={t('purchaseOrders.subtitle')}
        actions={(
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            {t('purchaseOrders.createPO')}
          </Button>
        )}
      />

      <StatTiles stats={[
        { label: t('purchaseOrders.stats.matching', { defaultValue: 'Matching orders' }), value: total },
        { label: t('purchaseOrders.stats.draft', { defaultValue: 'Draft on page' }), value: purchaseOrders.filter((po) => po.status === 'DRAFT').length },
        { label: t('purchaseOrders.stats.approved', { defaultValue: 'Approved on page' }), value: purchaseOrders.filter((po) => po.status === 'APPROVED').length, color: 'text-success' },
        { label: t('purchaseOrders.stats.completed', { defaultValue: 'Completed on page' }), value: purchaseOrders.filter((po) => po.status === 'COMPLETED').length, color: 'text-foreground' },
        { label: t('purchaseOrders.stats.usd', { defaultValue: 'USD amount on page' }), value: purchaseOrders.filter((po) => (po.currency || 'USD') === 'USD').reduce((sum, po) => sum + (po.totalAmount || 0), 0).toLocaleString() },
        { label: t('purchaseOrders.stats.inr', { defaultValue: 'INR amount on page' }), value: purchaseOrders.filter((po) => (po.currency || 'USD') === 'INR').reduce((sum, po) => sum + (po.totalAmount || 0), 0).toLocaleString() },
      ]} />

      <Card>
        <CardHeader>
          <CardTitle>{t('purchaseOrders.allPOs')}</CardTitle>
          <CardDescription>
            {t('purchaseOrders.description', { count: total })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <FilterBar>
            <SelectFilter
              label={t('purchaseOrders.table.status')}
              value={statusFilter}
              onChange={setStatusFilter}
              options={['DRAFT', 'PENDING', 'SUBMITTED', 'APPROVED', 'REJECTED', 'COMPLETED', 'CANCELLED'].map((v) => ({ value: v, label: v }))}
            />
            <SelectFilter
              label={t('purchaseOrders.table.customer')}
              value={customerFilter}
              onChange={setCustomerFilter}
              width="w-56"
              options={customers.map((c): Option => ({ value: c.id, label: c.name }))}
            />
            <SelectFilter
              label={t('purchaseOrders.table.partner')}
              value={partnerFilter}
              onChange={setPartnerFilter}
              width="w-56"
              options={partners.map((p): Option => ({ value: p.id, label: p.name }))}
            />
            <DateRangeFilter label="Ordered" value={dateRange} onChange={setDateRange} />
            <ClearFilters onClick={clearFilters} />
          </FilterBar>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('purchaseOrders.table.poNumber')}</TableHead>
                <TableHead>{t('purchaseOrders.table.customer')}</TableHead>
                <TableHead>{t('purchaseOrders.table.partner')}</TableHead>
                <TableHead>{t('purchaseOrders.table.amount')}</TableHead>
                <TableHead>{t('purchaseOrders.table.status')}</TableHead>
                <TableHead>{t('purchaseOrders.table.created')}</TableHead>
                <TableHead className="text-right">{t('users.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {purchaseOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground">
                    {t('dashboard.noData')}
                  </TableCell>
                </TableRow>
              ) : (
                purchaseOrders.map((po) => (
                  <TableRow key={po.id}>
                    <TableCell className="font-medium">{po.poNumber}</TableCell>
                    <TableCell>{po.customerName || <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell>{po.partnerName || '-'}</TableCell>
                    <TableCell>{(po.currency || 'USD')} {po.totalAmount.toLocaleString()}</TableCell>
                    <TableCell>
                      <StatusBadge status={po.status} />
                    </TableCell>
                    <TableCell>
                      {formatDate(po.created_at || po.createdAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => openViewDialog(po)}>
                          {t('purchaseOrders.view')}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(po)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteDialog(po)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <Pagination page={page} pageSize={DEFAULT_PAGE_SIZE} total={total} onPageChange={setPage} label="purchase orders" />
        </CardContent>
      </Card>

      {/* Create Purchase Order Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('purchaseOrders.dialogs.create.title')}</DialogTitle>
            <DialogDescription>
              {t('purchaseOrders.dialogs.create.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="poNumber">{t('purchaseOrders.dialogs.create.poNumber')}</Label>
              <Input
                id="poNumber"
                value={newPO.poNumber}
                onChange={(e) => setNewPO({ ...newPO, poNumber: e.target.value })}
                placeholder={t('purchaseOrders.dialogs.create.poNumberPlaceholder')}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="customer">{t('purchaseOrders.dialogs.create.customer')}</Label>
              <Select
                value={newPO.customerId}
                onValueChange={(value) => setNewPO({ ...newPO, customerId: value })}
              >
                <SelectTrigger id="customer">
                  <SelectValue placeholder="Select a customer" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="partner">{t('purchaseOrders.dialogs.create.partnerOptional')}</Label>
              <Select
                value={newPO.partnerId || 'none'}
                onValueChange={(value) => setNewPO({ ...newPO, partnerId: value === 'none' ? '' : value })}
              >
                <SelectTrigger id="partner">
                  <SelectValue placeholder="No partner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No partner</SelectItem>
                  {partners.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="amount">{t('purchaseOrders.table.amount')} ($)</Label>
              <Input
                id="amount"
                type="number"
                min="0"
                step="0.01"
                value={newPO.amount}
                onChange={(e) => setNewPO({ ...newPO, amount: parseFloat(e.target.value) || 0 })}
                placeholder="0.00"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="status">{t('purchaseOrders.table.status')}</Label>
              <Select
                value={newPO.status}
                onValueChange={(value) => setNewPO({ ...newPO, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PENDING">{t('purchaseOrders.status.pending')}</SelectItem>
                  <SelectItem value="APPROVED">{t('purchaseOrders.status.approved')}</SelectItem>
                  <SelectItem value="REJECTED">{t('purchaseOrders.status.rejected')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleCreatePO} disabled={!newPO.poNumber || !newPO.customerId}>
              {t('purchaseOrders.createPO')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Purchase Order Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('purchaseOrders.dialogs.view.title')}</DialogTitle>
            <DialogDescription>
              {t('purchaseOrders.dialogs.view.description')}
            </DialogDescription>
          </DialogHeader>
          {selectedPO && (
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>{t('purchaseOrders.table.poNumber')}</Label>
                <div className="text-sm font-medium">{selectedPO.poNumber}</div>
              </div>
              <div className="grid gap-2">
                <Label>{t('purchaseOrders.table.customer')}</Label>
                <div className="text-sm">{selectedPO.customerName}</div>
              </div>
              <div className="grid gap-2">
                <Label>{t('purchaseOrders.table.partner')}</Label>
                <div className="text-sm">{selectedPO.partnerName || '-'}</div>
              </div>
              <div className="grid gap-2">
                <Label>{t('purchaseOrders.table.amount')}</Label>
                <div className="text-sm">${selectedPO.totalAmount.toFixed(2)}</div>
              </div>
              <div className="grid gap-2">
                <Label>{t('purchaseOrders.table.status')}</Label>
                <div>
                  <StatusBadge status={selectedPO.status} />
                </div>
              </div>
              <div className="grid gap-2">
                <Label>{t('purchaseOrders.table.created')}</Label>
                <div className="text-sm">
                  {formatDateTime(selectedPO.created_at || selectedPO.createdAt)}
                </div>
              </div>
              {(selectedPO.approved_at || selectedPO.approvedAt) && (
                <div className="grid gap-2">
                  <Label>{t('purchaseOrders.status.approved')}</Label>
                  <div className="text-sm">
                    {formatDateTime(selectedPO.approved_at || selectedPO.approvedAt)}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setIsViewDialogOpen(false)}>
              {t('common.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Purchase Order Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('purchaseOrders.dialogs.edit.title')}</DialogTitle>
            <DialogDescription>
              {t('purchaseOrders.dialogs.edit.description')}
            </DialogDescription>
          </DialogHeader>
          {selectedPO && (
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-poNumber">{t('purchaseOrders.table.poNumber')}</Label>
                <Input
                  id="edit-poNumber"
                  value={selectedPO.poNumber}
                  onChange={(e) => setSelectedPO({ ...selectedPO, poNumber: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-customer">{t('purchaseOrders.table.customer')}</Label>
                <Select
                  value={selectedPO.customerId || ''}
                  onValueChange={(value) => setSelectedPO({ ...selectedPO, customerId: value })}
                >
                  <SelectTrigger id="edit-customer">
                    <SelectValue placeholder={selectedPO.customerName || 'Select a customer'} />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-partner">{t('purchaseOrders.dialogs.create.partnerOptional')}</Label>
                <Select
                  value={selectedPO.partnerId || 'none'}
                  onValueChange={(value) => setSelectedPO({ ...selectedPO, partnerId: value === 'none' ? undefined : value })}
                >
                  <SelectTrigger id="edit-partner">
                    <SelectValue placeholder={selectedPO.partnerName || 'No partner'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No partner</SelectItem>
                    {partners.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-amount">{t('purchaseOrders.table.amount')} ($)</Label>
                <Input
                  id="edit-amount"
                  type="number"
                  min="0"
                  step="0.01"
                  value={selectedPO.totalAmount}
                  onChange={(e) => setSelectedPO({ ...selectedPO, totalAmount: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-status">{t('purchaseOrders.table.status')}</Label>
                <Select
                  value={selectedPO.status}
                  onValueChange={(value) => setSelectedPO({ ...selectedPO, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DRAFT">{t('purchaseOrders.status.draft')}</SelectItem>
                    <SelectItem value="SUBMITTED">{t('purchaseOrders.status.submitted')}</SelectItem>
                    <SelectItem value="APPROVED">{t('purchaseOrders.status.approved')}</SelectItem>
                    <SelectItem value="REJECTED">{t('purchaseOrders.status.rejected')}</SelectItem>
                    <SelectItem value="COMPLETED">{t('purchaseOrders.status.completed')}</SelectItem>
                    <SelectItem value="CANCELLED">{t('purchaseOrders.status.cancelled')}</SelectItem>
                    <SelectItem value="PENDING">{t('purchaseOrders.status.pending')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleEditPO}>
              {t('common.update')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Purchase Order Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('purchaseOrders.dialogs.delete.title')}</DialogTitle>
            <DialogDescription>
              {t('purchaseOrders.dialogs.delete.description', { poNumber: selectedPO?.poNumber })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button variant="destructive" onClick={handleDeletePO}>
              {t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default PurchaseOrders;
